import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb, { getBreadcrumbSchema } from '@/components/feature/Breadcrumb';
import { aboutInfo, timelineEvents } from '@/mocks/about';

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    formData.delete('phone_alt');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value.toString());
      });

      const res = await fetch('https://readdy.ai/api/form/d9n3lfgbdkpk99j7gg00', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        if (typeof msg === 'string' && msg.includes('spam')) {
          setFormStatus('success');
          return;
        }
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: aboutInfo.brandName,
        url: 'https://www.shenmashipin.com',
        description: aboutInfo.description,
        email: aboutInfo.contact.email,
        telephone: aboutInfo.contact.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: aboutInfo.contact.address,
          addressLocality: '北京',
          addressRegion: '朝阳区',
          addressCountry: 'CN',
        },
        sameAs: [
          'https://weibo.com/shenmashipin',
        ],
      },
      {
        '@type': 'WebSite',
        name: aboutInfo.brandName,
        url: 'https://www.shenmashipin.com',
      },
      {
        '@type': 'AboutPage',
        name: `关于${aboutInfo.brandName} - 品牌介绍与联系方式`,
        description: aboutInfo.description,
        url: 'https://www.shenmashipin.com/about',
        isPartOf: {
          '@type': 'WebSite',
          name: aboutInfo.brandName,
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: aboutInfo.faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
      getBreadcrumbSchema(breadcrumbItems),
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`关于${aboutInfo.brandName} - 品牌介绍、口碑评价与联系方式 | 神马视频`}
        description={`${aboutInfo.description.slice(0, 120)}。了解${aboutInfo.brandName}的品牌故事、发展历程、用户口碑和联系方式。`}
        keywords="神马视频,关于我们,品牌介绍,联系方式,影视导航平台,口碑评价"
        canonicalUrl="https://www.shenmashipin.com/about"
        ogType="website"
        schema={pageSchema}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb />

          <section className="px-4 md:px-6 py-12 md:py-16 bg-background-50">
            <div className="max-w-[900px] mx-auto text-center">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                关于{aboutInfo.brandName}
              </h1>
              <p className="text-lg text-foreground-500 mb-6 max-w-[600px] mx-auto leading-relaxed">
                {aboutInfo.brandSlogan}
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed max-w-[700px] mx-auto">
                {aboutInfo.description}
              </p>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-[1000px] mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                品牌价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {aboutInfo.values.map((val) => (
                  <div key={val.title} className="bg-background-50 rounded-xl p-6 text-center">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                      <i className={`${val.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">
                      {val.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      {val.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-[1000px] mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                用户口碑
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                {aboutInfo.reputation.map((rep) => (
                  <div key={rep.label} className="text-center">
                    <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">
                      {rep.metric}
                    </div>
                    <div className="text-sm text-foreground-500">{rep.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-[800px] mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 -translate-x-1/2"></div>
                <div className="space-y-8">
                  {timelineEvents.map((event, idx) => (
                    <div
                      key={event.year}
                      className={`relative flex flex-col md:flex-row gap-4 ${
                        idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="hidden md:block flex-1"></div>
                      <div className="relative flex items-center justify-center z-10">
                        <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                          <div className="w-3 h-3 rounded-full bg-background-50"></div>
                        </div>
                      </div>
                      <div className="flex-1 bg-background-50 rounded-xl p-5">
                        <span className="text-xs text-primary-600 font-medium">{event.year}</span>
                        <h3 className="font-heading text-base font-bold text-foreground-900 mt-1 mb-2">
                          {event.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">
                          {event.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-[1000px] mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                常见问题
              </h2>
              <div className="max-w-[700px] mx-auto space-y-3">
                {aboutInfo.faq.map((item, idx) => (
                  <details key={idx} className="group bg-background-100 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-800 hover:text-foreground-900">
                      {item.question}
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-xs text-foreground-600 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-[800px] mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">电子邮件</p>
                      <a href={`mailto:${aboutInfo.contact.email}`} className="text-sm text-foreground-500 hover:text-primary-600 transition-colors" rel="nofollow">
                        {aboutInfo.contact.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">客服电话</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">公司地址</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">工作时间</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.workHours}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl p-6">
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-4">
                    发送消息
                  </h3>
                  <form
                    data-readdy-form
                    id="contact-form"
                    action="https://readdy.ai/api/form/d9n3lfgbdkpk99j7gg00"
                    method="POST"
                    encType="application/x-www-form-urlencoded"
                    onSubmit={handleSubmit}
                  >
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          姓名
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          邮箱
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
                          placeholder="请输入您的留言内容（最多500字符）"
                        ></textarea>
                      </div>
                      <button
                        type="submit"
                        className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                      >
                        发送消息
                      </button>
                    </div>
                    <div className="spam-guard">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                  </form>
                  {formStatus === 'success' && (
                    <div className="mt-4 p-3 rounded-lg bg-secondary-100 text-secondary-700 text-xs text-center">
                      <i className="ri-check-line mr-1"></i>
                      消息已发送成功，我们会尽快回复您！
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="mt-4 p-3 rounded-lg bg-primary-100 text-primary-700 text-xs">
                      {formError}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    </>
  );
}